import groq from 'groq';
import { SLUG } from 'lib/queries';
import { PLP_HREF } from 'lib/queries/common/plpHref';
import { SciencePageId } from 'types/cms/science/SciencePageId';

import { SUPPORT_EMAIL, SUPPORT_PHONE, SUPPORT_PORTAL } from './organization';
import * as routes from './routes';
import { BODY, MAIN, SKIN } from './science';

// Label, href, name (for tracking)
export const SHOP = {
  label: 'Shop all',
  href: routes.SHOP,
  name: 'shop_all',
};

export const SCIENCE = {
  label: 'Science of Mitopure®',
  href: routes.SCIENCE,
  name: 'science',
  category: MAIN,
};

export const HOW_IT_WORKS = {
  label: 'How Mitopure® works',
  href: routes.SCIENCE_HOW_IT_WORKS,
  name: 'how_it_works',
};

export const BENEFITS = {
  label: 'Benefits',
  href: routes.BENEFITS,
  name: 'benefits',
};

export const MITOPURE = {
  label: 'Mitopure',
  href: routes.MITOPURE,
  name: 'science_mitopure',
};

export const SCIENCE_BODY = {
  label: 'Benefits for body',
  href: routes.SCIENCE_BODY,
  name: 'science_body',
  category: BODY,
};

export const SCIENCE_SKIN = {
  label: 'Benefits for skin',
  href: routes.SCIENCE_SKIN,
  name: 'science_skin',
  category: SKIN,
};

export const ABOUT = {
  label: 'About Us',
  href: routes.ABOUT,
  name: 'about',
};

export const STUDIES = {
  label: 'Studies',
  href: routes.STUDIES,
  name: 'studies',
};

export const BLOG = {
  label: 'Blog',
  href: routes.BLOG,
  name: 'blog',
};

export const REVIEWS = {
  label: 'Reviews',
  href: routes.REVIEWS,
  name: 'reviews',
};

export const TESTIMONIALS = {
  label: 'Testimonials',
  href: routes.TESTIMONIALS,
  name: 'testimonials',
};

export const FAQ = {
  label: 'FAQs',
  href: routes.FAQ,
  name: 'faq',
};

export const TERMS = {
  label: 'Terms & Conditions',
  href: routes.TERMS,
  name: 'terms',
};

export const PRIVACY_POLICY = {
  label: 'Privacy Policy',
  href: routes.PRIVACY_POLICY,
  name: 'privacy_policy',
};

export const PATENTS = {
  label: 'Patents',
  href: routes.PATENTS,
  name: 'patents',
};

export const SHIPPING_POLICY = {
  label: 'Shipping Policy',
  href: routes.SHIPPING_POLICY,
  name: 'shipping_policy',
};

export const HELP = {
  label: 'Help Center',
  href: SUPPORT_PORTAL,
  name: 'help_center',
};

export const EMAIL = {
  label: SUPPORT_EMAIL,
  href: `mailto:${SUPPORT_EMAIL}`,
  name: 'email',
};

export const PHONE = {
  label: SUPPORT_PHONE,
  href: `tel:${SUPPORT_PHONE}`,
  name: 'phone',
};

export const AFFILIATE_PROGRAM = {
  label: 'Affiliate Program',
  href: routes.AFFILIATE_PROGRAM,
  name: 'affiliate_program',
};

export const INTERNATIONAL_WHOLESALE = {
  label: 'International Wholesale',
  href: 'mailto:international@timeline.com',
  name: 'international_wholesale',
};

// Skincare
export const SKINCARE = {
  label: 'Topicals',
  href: routes.SKINCARE_PATH,
  name: 'skincare',
  showIcon: true,
};

export const SKINCARE_ABOUT = {
  label: 'About Us',
  href: routes.SKINCARE_ABOUT,
  name: 'skincare_about',
};

export const SKINCARE_SCIENCE = {
  type: 'link' as const,
  label: 'Science',
  href: routes.SKINCARE_SCIENCE,
  name: 'skincare_science',
};

export const SKINCARE_BENEFITS = {
  label: 'Benefits',
  href: routes.SKINCARE_BENEFITS,
  name: 'skincare_benefits',
};

export const SKINCARE_REVIEWS = {
  label: 'Reviews',
  href: routes.SKINCARE_REVIEWS,
  name: 'skincare_reviews',
};

export const SKINCARE_SHOP = {
  label: 'Shop all',
  href: routes.SKINCARE_SHOP,
  name: 'skincare_shop_all',
};

export const SKINCARE_INGREDIENTS = {
  label: 'Ingredients',
  href: routes.SKINCARE_INGREDIENTS,
  name: 'skincare_ingredients',
};

export const BLOG_SKINCARE = {
  label: 'Blog',
  href: routes.BLOG_SKINCARE,
  name: 'blog_skincare',
};

export const SKINCARE_TERMS = {
  label: 'Terms & Conditions',
  href: routes.SKINCARE_TERMS,
  name: 'skincare_terms',
};

export const SKINCARE_PRIVACY_POLICY = {
  label: 'Privacy Policy',
  href: routes.SKINCARE_PRIVACY_POLICY,
  name: 'skincare_privacy_policy',
};

export const SKINCARE_SHIPPING_POLICY = {
  label: 'Shipping Policy',
  href: routes.SKINCARE_SHIPPING_POLICY,
  name: 'skincare_shipping_policy',
};

export const HCP = {
  type: 'link' as const,
  label: 'For Providers',
  href: 'https://pro.timeline.com',
  name: 'hcp',
  isExternal: true,
  showIcon: true /* Display ArrowUpRight in nav */,
};

export const AMAZENTIS = {
  type: 'link' as const,
  label: 'Amazentis',
  href: 'https://amazentis.com',
  name: 'amazentis',
  isExternal: true,
  showIcon: true /* Display ArrowUpRight in nav */,
};

export const LONGEVITY_CLUB = {
  label: 'Rewards',
  href: routes.LONGEVITY_CLUB,
  name: 'longevity_club',
};

// Navs
export const SHOP_NAV = {
  label: 'Shop',
  contentType: 'products',
  /* subNav is defined in queryNavGroup as this nav item is dynamic */
};

/* Old */
export const SCIENCE_NAV = {
  type: 'dropdown' as const,
  label: 'Science',
  subNav: {
    contentType: 'science',
    items: [],
  },
};

export const ABOUT_NAV = {
  type: 'dropdown' as const,
  label: 'About',
  subNav: {
    contentType: 'posts',
    items: [ABOUT, REVIEWS, TESTIMONIALS, BLOG, FAQ, AMAZENTIS],
  },
};

export const FOOTER_ABOUT_NAV = {
  ...ABOUT_NAV,
  subNav: {
    ...ABOUT_NAV.subNav,
    items: [ABOUT, REVIEWS, TESTIMONIALS, BLOG, FAQ, HCP, AMAZENTIS],
  },
};

export const LEGAL_NAV = {
  label: 'Legal',
  subNav: {
    items: [TERMS, PRIVACY_POLICY],
  },
};

export const LONGEVITY_GAME = {
  type: 'link' as const,
  label: 'Longevity Game',
  href: 'https://play.timeline.com',
  name: 'longevity_game',
  isExternal: true,
};

export const SUPPORT_NAV = {
  label: 'Support',
  subNav: {
    items: [
      HELP,
      EMAIL,
      PHONE,
      LONGEVITY_CLUB,
      AFFILIATE_PROGRAM,
      INTERNATIONAL_WHOLESALE,
    ],
  },
};

// Skincare
export const SKINCARE_EXPLORE_NAV = {
  type: 'dropdown' as const,
  label: 'Explore',
  subNav: {
    contentType: 'posts',
    items: [
      SKINCARE_SCIENCE,
      SKINCARE_BENEFITS,
      SKINCARE_ABOUT,
      SKINCARE_INGREDIENTS,
      BLOG_SKINCARE,
      SKINCARE_REVIEWS,
    ],
  },
};

export const SKINCARE_LEGAL_NAV = {
  label: 'Legal',
  subNav: {
    items: [SKINCARE_TERMS, SKINCARE_PRIVACY_POLICY],
  },
};

export const SKINCARE_BUILD_YOUR_ROUTINE_NAV = {
  label: 'Skin longevity quiz',
  name: 'skincare_build_my_routine',
  href: '/skincare/routine/',
};

export const SCIENCE_PAGE_MAP = {
  [SciencePageId.SCIENCE]: SCIENCE,
  [SciencePageId.HOW_IT_WORKS]: HOW_IT_WORKS,
  [SciencePageId.BENEFITS]: BENEFITS,
  [SciencePageId.BENEFITS_SKINCARE]: SKINCARE_BENEFITS,
  [SciencePageId.STUDIES]: STUDIES,
  [SciencePageId.PATENTS]: PATENTS,
};

export const buildScienceNav = (
  scienceNavigation: { _id: SciencePageId }[] | null | undefined,
) => {
  if (!scienceNavigation) return SCIENCE_NAV;

  const items = scienceNavigation
    .map(({ _id }) => SCIENCE_PAGE_MAP[_id])
    .filter(Boolean);

  return {
    ...SCIENCE_NAV,
    subNav: {
      ...SCIENCE_NAV.subNav,
      items,
    },
  };
};

export const queryNavGroup = ({ label, contentType }: typeof SHOP_NAV) => {
  const formattedLabel = label.toLowerCase();

  const query = groq`
    "groupLabel": label,
    "label": "${label}",
    "subNav": {
      "contentType": "${contentType}",
      "items": plpMenu[]-> {
        ${SLUG},
        ${PLP_HREF},
        label,
        "name": select(
          defined(slug.current) => "${formattedLabel}_" + slug.current,
          "${formattedLabel}_all"
        )
      }
    }`;

  return query;
};
